import { jsPDF } from "jspdf";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import { layoutSection } from "@/app/ui/modules/receipt-v2/layout/index";
import { uploadFile } from "@/app/ui/modules/receipt-v2/auto-print";
import messageModal from "@/app/infrastructures/misc/common-library/MessageModal";
import { logoBlack } from "@/app/ui/modules/receipt-v2/constants/images";
import { jsPDFDocument } from "jspdf-autotable";
import {
  KulionerMerchant,
  KulionerOrder,
  KulionerProduct
} from "@/domain/entities/Kulioner";
import autoTable from "jspdf-autotable";

let doc: any = new jsPDF("p", "mm", "a6", true);
const icon: { [x: string]: any } = {
  cut: null as any,
  frozen: null as any,
  dry: null as any,
  pork: null as any
};
const productNameColumnWidth = 34;
const cellPadding = {
  left: 1.5,
  right: 1.5,
  top: 1.5,
  bottom: 1.5
};

const preloadImages = async (data: KulionerOrder): Promise<void> => {
  icon.cut = await import("@/app/ui/assets/images/black-cut.png");
  icon.frozen = await import("@/app/ui/assets/images/frozen.png");
  icon.dry = await import("@/app/ui/assets/images/dry.png");
  icon.pork = await import("@/app/ui/assets/images/pork.png");

  await Promise.all(
    data.merchants.map((merchant: KulionerMerchant) =>
      Promise.all(
        merchant.products.map((product: KulionerProduct) =>
          new Promise((resolve, reject) => {
            if (product.thumbnail) {
              const img = new Image();
              img.onload = resolve;
              img.onerror = reject;
              img.src = product.thumbnail;
            } else {
              resolve(null);
            }
          }).catch((error) => console.log(`Failed to preload image: ${error}`))
        )
      )
    )
  );
};


const formatProductCell = (doc: jsPDFDocument, product: any) => {

  const maxTextWidth =
    productNameColumnWidth - cellPadding.left - cellPadding.right;
  const ellipsis = "...";
  const maxLines = 2;

  const words = product.name.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = doc.getTextWidth(testLine);

    if (testWidth <= maxTextWidth) {
      currentLine = testLine;
    } else {
      lines.push(currentLine);
      currentLine = word;

      if (lines.length === maxLines - 1) {
        // Build second line with remaining words
        const remainingWords = [word, ...words.slice(words.indexOf(word) + 1)];
        let secondLine = "";
        for (const nextWord of remainingWords) {
          const testSecond = secondLine ? `${secondLine} ${nextWord}` : nextWord;
          if (doc.getTextWidth(testSecond) <= maxTextWidth) {
            secondLine = testSecond;
          } else {
            // Truncate and add ellipsis
            while (
              doc.getTextWidth(secondLine + ellipsis) > maxTextWidth &&
              secondLine.length > 0
            ) {
              secondLine = secondLine.slice(0, -1);
            }
            secondLine += ellipsis;
            break;
          }
        }
        lines.push(secondLine);
        break;
      }
    }
  }

  if (lines.length < maxLines && currentLine) {
    lines.push(currentLine);
  }

  // Extra lines for other fields
  const extraLines = [];
  if (product.handlingType) extraLines.push("");
  if (product.indexQuantity) extraLines.push("");

  const finalText = [...lines, ...extraLines].join("\n");

  return finalText;
};

export const renderTable = async (
  doc: jsPDFDocument,
  order: KulionerOrder
): Promise<void> => {
  await preloadImages(order);

  const productList: any[] = [];

  // Create the flattened list with original position info
  for (const [merchantIndex, m] of order.merchants.entries()) {
    const merchantProduct = m.products.filter((product) => product.actualQuantity)
    const merchantProductLength = merchantProduct.length;
    
    for (const [productIndex, product] of merchantProduct.entries()) {
      // Repeat product entry based on quantity
      const quantity = product.actualQuantity || 1;
      for (let i = 0; i < quantity; i++) {
        productList.push({
          globalPosition: `${productList.length + 1}/${order.merchants.reduce((sum, m) => 
            sum + m.products.filter((product) => product.actualQuantity).reduce((pSum, p) => pSum + (p.actualQuantity || 1), 0), 0)}`,
          name: product.name,
          handlingType: product.handlingType,
          quantity: product.actualQuantity,
          indexQuantity: i + 1,
          originalPosition: {
            index: productIndex,
            totalInMerchant: merchantProductLength,
            merchantIndex: merchantIndex,
            merchantName: m.name
          },
          productData: {
            id: product.id,
            price: product.price,
            thumbnail: product.thumbnail
          }
        });
      }
    }
  }

  const dataTable: string[] [] = productList
    .map((product, _) => [
      product.globalPosition,
      formatProductCell(
        doc,
        product
      ),
      String(order.orderSttNo)
    ]
  );

  autoTable(doc, {
    theme: "plain",
    columns: [
      { header: "No", key: "no" },
      { header: "Nama Produk", key: "productName" },
      { header: "Nomor STT", key: "sttNo" }
    ],
    body: dataTable,
    columnStyles: {
      no: {
        cellWidth: 12,
        halign: "center"
      },
      productName: {
        halign: "left"
      },
      sttNo: {
        cellWidth: 38,
        halign: "left"
      }
    },
    headStyles: {
      fontSize: 9,
      fontStyle: "bold",
      textColor: "#454545",
      minCellHeight: 5,
      valign: "middle",
      lineColor: "#A7A8AA",
      lineWidth: 0.1
    },
    styles: {
      font: "Montserrat",
      fontSize: 8,
      fontStyle: "bold",
      textColor: "#454545",
      valign: "middle",
      cellPadding
    },
    startY: 0,
    margin: { top: 0, left: 0, right: 0, bottom: 0 },
    pageBreak: "auto",
    willDrawCell: (data) => {
      const { doc, cell, section } = data;

      // Apply only to body rows
      if (section === "body") {
        doc.setDrawColor("#A7A8AA");
        doc.setLineWidth(0.1);

        // Draw solid vertical line (left edge of the cell)
        doc.setLineDash([]); // solid
        doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);

        // Draw dashed horizontal line (bottom edge)
        doc.setLineDash([1], 0); // dashed
        doc.line(
          cell.x,
          cell.y + cell.height,
          cell.x + cell.width,
          cell.y + cell.height
        );

        // Reset dash for future drawings
        doc.setLineDash([]);
      }
    },
    didDrawCell: (data) => {
      const { column, cell, row, doc, section } = data;

      if (section === "head" && column.dataKey === "no") {
        // Clear default text (optional: fill white rect over cell)
        doc.setFillColor("white");
        doc.rect(
          cell.x + 1, // padding start 1
          cell.y + 1, // padding start 1
          cell.width - 2, // padding end 1
          cell.height - 2, // padding end 1
          "F"
        );

        // Draw centered header text manually
        const text = data.cell.text[0]; // header text is an array
        const textWidth = doc.getTextWidth(text);
        const centerX = cell.x + cell.width / 2;
        const textX = centerX - textWidth / 2;
        const textY = cell.y + cell.height / 2 + 0.5; // adjust baseline
        doc.text(text, textX, textY);
      }

      if (data.section === "body" && column.dataKey === "no") {
        const imgWidth = 2.5;
        const imgHeight = 2.5;
        const spacing = 0.5; // space between icon and text

        // Get the text
        const text = (row.raw as any)?.[0];

        // Set font for measuring text width
        doc.setFont("Montserrat", "normal");
        doc.setFontSize(5);

        // Calculate start x to center total width in cell
        const startX = cell.x

        // Calculate y positions to vertically center icon and text
        const centerY = cell.y + cell.height;

        // Draw icon
        doc.addImage(
          icon.cut.default,
          "PNG",
          startX,
          centerY - imgHeight / 2,
          imgWidth,
          imgHeight,
          undefined,
          "FAST"
        );
      }

      if (section === "body" && column.dataKey === "productName") {
        const image: any = (status: string) => {
          const obj = {
            "DENGAN ICE": icon.frozen.default,
            "TIDAK BOLEH BASAH": icon.dry.default,
            "TANPA ICE": icon.dry.default,
          } as any
          return obj[status] ?? ""
        }
        const productIndex = row.index

        if (productList[productIndex].quantity) {
          const fontSize = 6;
          doc.setFontSize(fontSize);
          doc.setFont("Montserrat", "normal");
          doc.setTextColor("#454545");
          const text = `${productList[productIndex].indexQuantity}/${productList[productIndex].quantity}`;
          const cellText = formatProductCell(doc, productList[productIndex]);
          const totalLines = cellText.split("\n").length;
          const additionalHeight = totalLines !== 3 ? 2 : !productList[productIndex].handlingType ? 4 : 1;
          
          const startX = cell.x + cellPadding.left;
          const startY = cell.y + (cell.height / 2) + additionalHeight;

          doc.text(text, startX, startY);
        }

        if (productList[productIndex].handlingType) {
          // chip data
          const fontSize = 6;
          const chipHeight = fontSize - 2.5;
          const marginChipTop = 0.6;

          const startX = cell.x + cellPadding.left;
          const startY = cell.y + cell.height / 2;

          // chip data
          const chipIconWidth = 3;
          const chipIconHeight = 3;

          const cellText = formatProductCell(doc, productList[productIndex]);
          const totalLines = cellText.split("\n").length;
          const additionalHeight = totalLines === 3 ? 1.5 : 3 
          const chipY = startY + additionalHeight;

          // set text styling for accurate text width
          doc.setFontSize(fontSize);
          doc.setFont("Montserrat", "normal");
          doc.setTextColor("#FFF");
          const chipText = productList[productIndex].handlingType;
          const chipTextWidth = doc.getTextWidth(chipText);

          const chipPaddingX = 1.5;
          const chipSpaceContent = 0.5;

          const chipWidth =
            (chipPaddingX * 2) +
            (image(productList[productIndex].handlingType) ? chipIconWidth : 0) +
            chipSpaceContent +
            chipTextWidth;

          const chipContentX = startX + chipPaddingX;
          const chipIconY = chipY + chipHeight / 2 - chipIconHeight / 2;

          const chipTextX = chipContentX + (image(productList[productIndex].handlingType) ? chipIconWidth : 0) + chipSpaceContent;
          const chipTextY = chipY + marginChipTop + chipHeight / 2;

          cell.height = cell.height + chipHeight;
          // Chip background
          doc.setFillColor("#4D4D4D");
          doc.roundedRect(
            startX,
            chipY,
            chipWidth,
            chipHeight,
            1.75,
            1.75,
            "F"
          );

          // Chip icon
          if (image(productList[productIndex].handlingType)) {
            doc.addImage(
              image(productList[productIndex].handlingType),
              "PNG",
              chipContentX,
              chipIconY,
              chipIconWidth,
              chipIconHeight,
              undefined,
              "FAST"
            );
          }

          // Chip text
          doc.text(chipText, chipTextX, chipTextY);
        }
      }
    }
  });
};

const openTab = (docs: any) => {
  docs.setProperties({
    title: `resi-thermal.pdf`
  });

  docs.autoPrint();

  window.open(docs.output("bloburl"), "_blank");

  doc = new jsPDF("p", "mm", "a6", true); //reset after finish
};

const getBookingType = (item: PrintDataEntitas): string => {
  if (item.sttShipmentId) return "shipment";
  if (!item.sttShipmentId && !item.sttClientId) return "manual";
  return "client";
};

const handlePrinting = async (data: PrintDataEntitas[], doc: any) => {
  const print = await uploadFile(
    doc.output("blob"),
    `${data[0]?.sttNo}-bulk`,
    false
  );

  if (!print) {
    openTab(doc);
    messageModal(
      "Label Gagal Diprint",
      "circle-error",
      "Pastikan semua perangkat Anda telah terhubung. <br/> Cek & coba lagi"
    );
    return;
  }

  messageModal(
    "Label Berhasil Diprint",
    "badge-success",
    "Anda berhasil mencetak data yang diinginkan"
  );
};

const generateSttPages = async (
  data: PrintDataEntitas[],
  accountType: string,
  logo: any
) => {
  for (const [index, item] of data.entries()) {
    if (index > 0) {
      doc.addPage("a6", "p");
    }
    const language = item.sttProductType === "INTERPACK" ? "en" : "id";
    await layoutSection(
      {
        doc,
        bookingType: getBookingType(item),
        accountType,
        data: item,
        pieceData: item,
        barcode: item.barcode,
        vendorRefCode: item.vendorRefCode,
        barcodeValue: item.sttNo,
        vendorRefCodeValue: item.sttVendorRefferenceNo,
        logo,
        shipmentId: item.sttShipmentId,
        startX: 0,
        startY: 0
      },
      language
    );

    doc.addPage();
    await renderTable(doc, item.orderDetail);
  }
};

interface GenerateKulionerBulkLabelResiThermalProps {
  accountType: string;
  data: PrintDataEntitas[];
}
const generateKulionerBulkLabelResiThermal = async ({
  accountType,
  data
}: GenerateKulionerBulkLabelResiThermalProps) => {
  if (!data.length) {
    return;
  }

  doc = new jsPDF("p", "mm", "a6", true);
  const logo = logoBlack;

  await generateSttPages(data, accountType, logo);
  await handlePrinting(data, doc);
};

export default generateKulionerBulkLabelResiThermal;
